import { format } from "date-fns";
import { useState } from "react";

import { Card } from "@/components/card";
import { LoadingScreen } from "@/components/loading-screen";
import { useTableControl } from "@/components/table-control";
import { Badge } from "@/components/ui/badge";
import { Timeframe } from "@/features/reporting/types/timeframe";
import { generateTimeRange } from "@/features/reporting/utils/generate-time-range";

import { useGetRecordingViews } from "../../api/get-recording-views";
import { TimeframeSelector } from "../timeframe-selector";

import { RecordingViewsTable } from "./recording-views-table";

type RecordingViewsProps = {
  organisationId?: string;
};

export function RecordingViews({ organisationId }: RecordingViewsProps) {
  const [timeFrame, setTimeFrame] = useState<Timeframe>("w");
  const { pagination, setPagination } = useTableControl({
    initialPageSize: 5,
    persistInUrl: false,
  });
  const { from, to } = generateTimeRange(timeFrame);

  const {
    data: recordings,
    isLoading,
    isSuccess,
    isFetching,
  } = useGetRecordingViews({
    from: from.toISOString(),
    to: to.toISOString(),
    organisationId,
  });

  return (
    <Card>
      <div className="flex items-center justify-between">
        <Card.Header
          title={
            <span>
              Replays
              <Badge variant="outline" className="ml-1 font-normal">
                {format(from, "MMM dd")} - {format(to, "MMM dd")}
              </Badge>
            </span>
          }
        />
        <TimeframeSelector
          value={timeFrame}
          onChange={(value) => {
            setTimeFrame(value as Timeframe);
            setPagination((prev) => ({
              ...prev,
              pageIndex: 0,
            }));
          }}
        />
      </div>

      <div className="relative">
        {isLoading && (
          <LoadingScreen className="absolute inset-0 z-10 h-full w-full bg-background" />
        )}
        <RecordingViewsTable
          data={isSuccess ? recordings : []}
          pagination={pagination}
          onPaginationChange={setPagination}
          pageCount={1}
          isLoading={isLoading || isFetching}
        />
      </div>
    </Card>
  );
}
